import { TFuncKey } from 'react-i18next'

export enum UserOrganizationStatuses {
  PendingActivation = 'PendingActivation',
  Active = 'Active',
}

export enum AccessLevel {
  Organization = 'Organization',
  Team = 'Team',
}

export enum AccessTypes {
  Admin = 'Admin',
  Editor = 'Editor',
  Viewer = 'Viewer',
}

export enum UserStatuses {
  Active = 'Active',
  Inactive = 'Inactive',
  JoinRequested = 'JoinRequested',
  Invited = 'Invited',
}

export enum DiscoverabilityTypes {
  Public = 'Public',
  Private = 'Private',
  Hidden = 'Hidden',
}

export enum ResumeBuilderStateEnum {
  NoFile = 'editor.states.noFile',
  FileLoading = 'editor.states.fileLoading',
  FileLoaded = 'editor.states.fileLoaded',
  Uploading = 'editor.states.uploading',
  Parsing = 'editor.states.parsing',
  Done = 'editor.states.done',
  Error = 'editor.states.error',
}

export enum ResumeQuickfixStateEnum {
  NoFile = 'quickfix.states.noFile',
  FileLoading = 'quickfix.states.fileLoading',
  FileLoaded = 'quickfix.states.fileLoaded',
  Processing = 'quickfix.states.processing',
  Done = 'quickfix.states.done',
  Error = 'quickfix.states.error',
}

export enum FeatureFlags {
  DynamicSections = 'dynamicSections',
}

export enum ServicePlanChargeTypes {
  PayAsYouGo = 'PayAsYouGo',
  UsageCount = 'UsageCount',
}

export enum UserOrganizationErrorCodes {
  UserOrganizationAlreadyExists = 'UserOrganizationAlreadyExists',
  UserOrganizationMaxUsersReached = 'UserOrganizationMaxUsersReached',
  UserOrganizationExceedsServicePlanUsersLimit = 'UserOrganizationExceedsServicePlanUsersLimit',
  UserAlreadyInAnOrganization = 'UserAlreadyInAnOrganization',
  UserAlreadyExistsInTeam = 'UserAlreadyExistsInTeam',
  TeamAlreadyExists = 'TeamAlreadyExists',
  UserOrganizationActionNotAllowed = 'UserOrganizationActionNotAllowed',
  PublicEmailDomainNotAllowed = 'PublicEmailDomainNotAllowed',
  CannotRemoveSelf = 'CannotRemoveSelf',
  CannotRemoveOnlyAdmin = 'CannotRemoveOnlyAdmin',
}

export const UserOrganizationErrorMapping: Record<string, TFuncKey> = {
  [UserOrganizationErrorCodes.UserOrganizationAlreadyExists]:
    'joinOrganization.orgExistsError',
  [UserOrganizationErrorCodes.UserOrganizationMaxUsersReached]:
    'usersAndTeams.maxUsersReachedError',
  [UserOrganizationErrorCodes.UserOrganizationExceedsServicePlanUsersLimit]:
    'pricingAndPlans.tooManyUsersError',
  [UserOrganizationErrorCodes.UserAlreadyInAnOrganization]:
    'usersAndTeams.userInOtherOrgError',
  [UserOrganizationErrorCodes.UserAlreadyExistsInTeam]:
    'usersAndTeams.userInTeamError',
  [UserOrganizationErrorCodes.TeamAlreadyExists]:
    'usersAndTeams.teamAlreadyExistsError',
  [UserOrganizationErrorCodes.UserOrganizationActionNotAllowed]:
    'usersAndTeams.actionRestrictedError',
  [UserOrganizationErrorCodes.PublicEmailDomainNotAllowed]:
    'joinOrganization.publicDomainsNotAllowedError',
  [UserOrganizationErrorCodes.CannotRemoveSelf]:
    'usersAndTeams.cannotRemoveSelfError',
  [UserOrganizationErrorCodes.CannotRemoveOnlyAdmin]:
    'usersAndTeams.cannotRemoveOnlyAdminError',
}

export enum ResumeTemplateErrorCodes {
  TemplateAlreadyExists = 'TemplateAlreadyExists',
}

export const ResumeTemplateErrorMapping: Record<string, TFuncKey> = {
  [ResumeTemplateErrorCodes.TemplateAlreadyExists]:
    'templates.newTemplate.templateExistsError',
}

export enum TemplateStatuses {
  Active = 'Active',
  Pending = 'Pending',
  EditRequest = 'EditRequest',
}

export enum ReportProblemErrorCodes {
  ImageFileExceedsSizeLimit = 'ImageFileExceedsSizeLimit',
  VideoFileExceedsSizeLimit = 'VideoFileExceedsSizeLimit',
  DocFileExceedsSizeLimit = 'DocFileExceedsSizeLimit',
  UnsupportedFileType = 'UnsupportedFileType',
}

export const ReportProblemErrorMapping: Record<string, TFuncKey> = {
  [ReportProblemErrorCodes.ImageFileExceedsSizeLimit]:
    'reportProblem.imageFileSizeExceedsLimitError',
  [ReportProblemErrorCodes.VideoFileExceedsSizeLimit]:
    'reportProblem.videoFileSizeExceedsLimitError',
  [ReportProblemErrorCodes.DocFileExceedsSizeLimit]:
    'reportProblem.docFileExceedsSizeLimitError',
  [ReportProblemErrorCodes.UnsupportedFileType]:
    'reportProblem.unsupportedFileTypeError',
}

export enum PricingPlans {
  UnverifiedUser = 'UnverifiedUser',
  FreeTrial = 'Free',
  FreeLegacy = 'FreeLegacy',
  Starter = 'Starter',
  Pro = 'Pro',
  Enterprise = 'Enterprise',
}

export enum CancellationReasons {
  NoLongerNeed = 'NoLongerNeed',
  PricingConcerns = 'PricingConcerns',
  FoundAlternativeSolution = 'FoundAlternativeSolution',
  Unsatisfied = 'Unsatisfied',
  Other = 'Other',
}

export enum BHNewTabMessageType {
  Ready = 'NewTabReady',
}

export enum AiMenuItems {
  Confirm = 'confirm',
  Discard = 'discard',
  FixGrammar = 'fixGrammar',
  MakeShorter = 'makeShorter',
  MakeLonger = 'makeLonger',
  BulletPoints = 'bulletPoints',
  ChangeVoice = 'changeVoice',
  FirstPerson = 'firstPerson',
  ThirdPerson = 'thirdPerson',
  Translate = 'translate',
  TranslateEnglish = 'translateEnglish',
  TranslateFrench = 'translateFrench',
  ChangeTense = 'changeTense',
  PresentTense = 'presentTense',
  PastTense = 'pastTense',
}

export enum ResumeBuilderAIActionEnum {
  Custom = 'custom',
  FixGrammar = 'fixGrammar',
  MakeShorter = 'makeShorter',
  MakeLonger = 'makeLonger',
  BulletPoints = 'bulletPoints',
  FirstPerson = 'firstPerson',
  ThirdPerson = 'thirdPerson',
  TranslateEnglish = 'translateEnglish',
  TranslateFrench = 'translateFrench',
  PresentTense = 'presentTense',
  PastTense = 'pastTense',
}

export const ResumeTabKey = 'resumeTab'

export enum ResumeFormat {
  Pdf = 'pdf',
  Docx = 'docx',
}

export enum BHEntityTypes {
  Candidate = 'Candidate',
  JobPosting = 'Job Posting',
}
